import AppointmentDetails from 'tailwind-components/AppointmentDetails/AppointmentDetails';
import Button from 'tailwind-components/Button/Button';
import RequiredActionsModal from 'components/RequiredActionsModal/RequiredActionsModal';
import axios from 'axios';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';
import { Info, TriangleAlert } from 'lucide-react';
import { Stack, Tooltip } from '@mui/material';
import { STATE_LIST_CERT, TRANSFER_TYPES } from 'constants/enum';
import { isAgentRankLimited } from 'helpers/agents';
import { arrayDifference } from 'helpers/arrays';
import { getStateCodeFromStateName } from 'helpers/states';
import { sanitizeDirtyHTML } from 'helpers/strings';
import { scrollElementToContainerTop } from 'helpers/dom/scroll';
import { useDispatch, useSelector } from 'react-redux';
import { setRequestedCarrierName } from 'state/actions/appointments';
import { DISCLAIMER_PLACEMENTS } from 'helpers/factory/carrierFactory';
import companyNames from 'constants/companyNames';

import {
  getCarrierStateOptions,
  getBcbsCrossRequestBlockMessage,
  getCarrierDisabledDueToBlackoutData,
  isAgentAppointedWithCarrier,
  isAppointmentActionable,
  isCarrierBlockedByBcbsCrossRequest,
  isRecertificationRequest
} from 'helpers/appointments/appointments';
import { shouldBlockForFfmCertification } from 'helpers/appointments/ffmCertification';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  getCarrierRequiredActions,
  isBlackoutPeriod,
  KAISER_GA_REQUIRED_FIELDS,
  KAISER_GA_TRANSIENT_FIELDS
} from 'helpers/requiredActions/requiredActions';

import {
  getSuncareCardStatus,
  getSuncareTrainingBadgeClassName,
  getSuncareUiState,
  SUNCARE_PHASES,
  SUNCARE_REQUIRED_FIELDS,
  SUNCARE_TRANSIENT_FIELDS
} from 'helpers/suncare/suncare';
import {
  shouldUseSuncareSendContract,
  sendSuncareContractSent
} from 'state/actions/suncare';
import SuncareInProgressNotice from 'components/SuncareInProgressNotice/SuncareInProgressNotice';
import SuncareTrainingNotice from 'components/SuncareTrainingNotice/SuncareTrainingNotice';

import { getCompany } from 'helpers/getCompanyVariables';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import { AGENCY_DISABLED_CARRIER_MESSAGE } from 'helpers/carriers/carriers';
import TransferTypeSelector from './TransferTypeSelector';
import StateSelector from './StateSelector';
import { AppointmentMessages } from './AppointmentMessages';
import CarrierFeatureNotes from './CarrierFeatureNotes';
import ScrollToRequestButton from './ScrollToRequestButton';
import SendRequestButton from './SendRequestButton';
import SBEModal from './SBEModal';
import useIsElementInView from './useIsElementInView';

const company = getCompany();

const isOneill = company === 'oneill';

const INITIAL_VALUES = {
  appointments: [],
  appointedType: TRANSFER_TYPES.NewAppointment,
  files: [],
  expirationDates: [],
  acknowledgement: false,
  providenceAcknowledgement: false,
  bcbsNcAcknowledgement: false,
  bcbsScAcknowledgement: false,
  ...KAISER_GA_REQUIRED_FIELDS,
  ...SUNCARE_REQUIRED_FIELDS
};

// recertification requests have no state subform, so they get a fixed label instead of state codes
function getPendingApptBadgeLabel(appt) {
  if (isRecertificationRequest(appt)) return 'Recertification';
  if (appt.carrier === 'BCBS')
    return getStateCodeFromStateName(appt.uniqueBcbsState);
  return (appt.appointments_information || [])
    .map(row => getStateCodeFromStateName(row.Appointment_State_1))
    .filter(state => !!state)
    .join(', ');
}

const SideSection = forwardRef(function Component(
  {
    confirmed = [],
    pending = [],
    carrierConsidersStates,
    carrier,
    carrierData,
    statesOffered,
    createAppointmentRequest,
    obFormCompleted,
    simplified,
    setApptRequestStatus,
    carrierInProgress,
    hasOpenTransferRequests,
    areApptsDisabled,
    isAgencyDisabled
  },
  ref
) {
  const cancelTokenSource = axios.CancelToken.source();
  // Redux
  const { isSendingRequestAppointments, requestedAppointmentsByCarrier } =
    useSelector(state => state.appointments);

  const carrierReqAppts = requestedAppointmentsByCarrier[carrier] ?? [];

  // we don't want to miss an actionable required actions by defaulting to the first appt in the array
  const noStatesActionableAppt =
    carrierReqAppts.find(isAppointmentActionable) || carrierReqAppts[0];

  const { attachments } = useSelector(state => state.appointments);
  const user = useSelector(state => state.user.zohoData);

  const isAgentAppointed = isAgentAppointedWithCarrier(carrier, user);

  const isAgentLimited = isAgentRankLimited(user.Rank);

  const isFfmCertificationBlocked = shouldBlockForFfmCertification(
    carrierData?.type,
    user?.FFM_Certification_Expiration
  );

  const dispatch = useDispatch();
  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [isOpenSBEModal, setIsOpenSBEModal] = useState(false);
  const [isOpenRequiredActionsModal, setIsOpenRequiredActionsModal] =
    useState(false);
  // required actions are computed on demand at submit
  const [requiredActions, setRequiredActions] = useState({
    filteredActions: []
  });
  const [appointmentDetails, setAppointmentDetails] = useState(null);

  // the scroll lands on the section top, but visibility is driven by the send request button,
  // otherwise a sliver of the section title peeking in would already hide the shortcut
  const requestSectionRef = useRef(null);
  const sendRequestButtonRef = useRef(null);

  const scrollToRequestSection = () => {
    scrollElementToContainerTop(requestSectionRef.current);
  };

  const blackoutData = useMemo(
    () => ({
      transferBlackoutData: isBlackoutPeriod(
        carrierData.transferBlackout,
        formData.appointments
      ),
      contractingBlackoutData: isBlackoutPeriod(
        carrierData.contractingBlackout,
        formData.appointments
      )
    }),
    [carrierData, carrierData?.name, formData.appointments]
  );

  const { transferBlackoutData, contractingBlackoutData } = blackoutData;

  const { filteredActions } = requiredActions;

  const {
    isCarrier: isSuncareCarrier,
    isPendingDirector: isSuncarePendingDirector,
    isTrainingPending: isSuncareTrainingPending,
    isAppointmentBlocked: isSuncareAppointmentBlocked
  } = getSuncareUiState(user, carrier);
  const isSuncareContractStep = shouldUseSuncareSendContract(
    filteredActions,
    user
  );

  useEffect(() => {
    if (isSuncareCarrier) {
      setFormData(prevState => ({
        ...prevState,
        appointedType: TRANSFER_TYPES.NewAppointment
      }));
    }
  }, [carrier, isSuncareCarrier]);

  // derive blackout flags from the cheap isBlackoutPeriod memo, not from the full RA calc
  const {
    disabled: isCarrierDisabledDueToBlackout,
    message: carrierDisabledDueToBlackoutMessage
  } = getCarrierDisabledDueToBlackoutData(
    {
      isContractingBlackout: contractingBlackoutData.isBlackout,
      contractingBlackoutStates: contractingBlackoutData.states,
      isFullContractingBlackout:
        contractingBlackoutData.isBlackout &&
        contractingBlackoutData.states.length === 0,
      isTransferBlackout: transferBlackoutData.isBlackout,
      transferBlackoutStates: transferBlackoutData.states,
      isFullTransferBlackout:
        transferBlackoutData.isBlackout &&
        transferBlackoutData.states.length === 0
    },
    formData.appointments,
    formData.appointedType
  );

  const isBlockedByBcbsCrossRequest = isCarrierBlockedByBcbsCrossRequest(
    carrier,
    requestedAppointmentsByCarrier,
    formData.appointments
  );

  const bcbsCrossRequestBlockMessage = getBcbsCrossRequestBlockMessage(
    carrier,
    requestedAppointmentsByCarrier
  );

  // FFM states are already filtered out of `options`, so the remaining SBE states stay requestable.
  // carriers that don't break requests down by state have nothing to filter, so they stay blocked
  const isDisabled =
    !obFormCompleted ||
    (isFfmCertificationBlocked && !carrierConsidersStates) ||
    isAgencyDisabled ||
    (carrierConsidersStates && formData.appointments.length < 1) ||
    isSendingRequestAppointments ||
    isCarrierDisabledDueToBlackout ||
    isAgentLimited ||
    hasOpenTransferRequests ||
    areApptsDisabled ||
    isBlockedByBcbsCrossRequest;

  const handleChangeAppointedType = e => {
    setFormData(prevState => ({ ...prevState, appointedType: e.target.value }));
  };

  const handleAcknowledgement = (key, value) => {
    setFormData(prevState => ({
      ...prevState,
      [key]: value
    }));
  };

  const handleFilesChange = (index, files) => {
    setFormData(prevState => {
      const updatedFiles = [...prevState.files];
      updatedFiles[index] = files;
      return { ...prevState, files: updatedFiles };
    });
  };

  const handleExpirationDatesChange = (index, date) => {
    setFormData(prevState => {
      const updatedDates = [...prevState.expirationDates];
      updatedDates[index] = date;
      return { ...prevState, expirationDates: updatedDates };
    });
  };

  useEffect(() => {
    setApptRequestStatus &&
      setApptRequestStatus(_current => ({
        appointments: isSendingRequestAppointments
          ? _current.appointments
          : formData.appointments,
        disabled: isDisabled,
        loading: isSendingRequestAppointments
      }));
  }, [formData.appointments, isSendingRequestAppointments]);

  const handleChangeAppointments = values => {
    setFormData(prevState => ({
      ...prevState,
      appointments: values,
      files: [],
      expirationDates: [],
      acknowledgement: false,
      ...KAISER_GA_REQUIRED_FIELDS,
      ...SUNCARE_REQUIRED_FIELDS
    }));
  };

  // compute required actions on demand for the exact states being submitted (subset-aware)
  const computeRequiredActions = appointments =>
    getCarrierRequiredActions(
      carrierData,
      appointments,
      attachments,
      formData.appointedType,
      isBlackoutPeriod(carrierData.transferBlackout, appointments),
      isBlackoutPeriod(carrierData.contractingBlackout, appointments),
      user
    );

  const handleSubmit = (appointments, overrideSBEModal = false) => {
    if (isSuncareAppointmentBlocked) return;
    // OB form already gates the button (disabled), so it is not re-checked here
    const areCertStatesIncluded = STATE_LIST_CERT.some(({ state }) =>
      appointments.includes(state)
    );

    if (areCertStatesIncluded && !overrideSBEModal) {
      setIsOpenSBEModal(true);
      return;
    }

    // required actions computed once here, only for the exact states being submitted
    const nextRequiredActions = computeRequiredActions(appointments);
    setRequiredActions(nextRequiredActions);

    // narrow the request to the chosen states and size the required actions arrays for them
    setFormData(prevState => ({
      ...prevState,
      appointments,
      files: nextRequiredActions.filteredActions.map(() => null),
      expirationDates: nextRequiredActions.filteredActions.map(() => null),
      acknowledgement: false,
      ...KAISER_GA_REQUIRED_FIELDS,
      ...SUNCARE_REQUIRED_FIELDS
    }));

    if (nextRequiredActions.filteredActions.length > 0) {
      setIsOpenSBEModal(false);
      setIsOpenRequiredActionsModal(true);
      return;
    }

    handleSendRequest(appointments, nextRequiredActions);
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        handleRequestClick: () => handleSubmit(formData.appointments)
      };
    },
    [handleSubmit, formData.appointments]
  );

  const handleSendRequest = async (
    appointments,
    overrideRequiredActions = null
  ) => {
    setIsOpenSBEModal(false);
    // strip Kaiser GA transient fields (DOB/SSN) so they are not sent to the backend
    const sanitizedFormData = { ...formData };
    KAISER_GA_TRANSIENT_FIELDS.forEach(field => {
      delete sanitizedFormData[field];
    });
    SUNCARE_TRANSIENT_FIELDS.forEach(field => {
      delete sanitizedFormData[field];
    });

    const data = {
      ...sanitizedFormData,
      carrierName: carrier,
      states: appointments,
      transfer:
        isOneill &&
        carrierConsidersStates &&
        formData.appointedType === TRANSFER_TYPES.NewAppointment
          ? 'Adding States'
          : formData.appointedType,
      carrierData,
      files: formData.files,
      requiredActions: overrideRequiredActions ?? requiredActions,
      expirationDates: formData.expirationDates,
      attachments
    };

    // the following is NOT an error
    dispatch(setRequestedCarrierName(carrier)); // optimistically, make the request to get appointments data
    const isSuccess = await createAppointmentRequest(
      cancelTokenSource,
      data,
      typeof carrierInProgress === 'function'
        ? carrierInProgress(carrierData.id, appointments)
        : () => {}
    ); // add requested appointments
    if (isSuccess) {
      setFormData(INITIAL_VALUES); // reset the form state
      setIsOpenRequiredActionsModal(false); // close the file upload modal
    } else {
      dispatch(setRequestedCarrierName(carrier)); // dispatch action to force reload and stop component from being stuck in loading state
    }
  };

  const handleSendContract = async () => {
    if (!formData.suncareContractOpened) return;

    const result = await dispatch(sendSuncareContractSent());

    if (result?.ok) {
      setFormData(INITIAL_VALUES);
      setRequiredActions({ filteredActions: [] });
      setIsOpenRequiredActionsModal(false);
    }
  };

  useEffect(() => {
    setFormData(INITIAL_VALUES);
    setRequiredActions({ filteredActions: [] });
  }, [carrier]);

  const tempOptions = arrayDifference(
    carrierConsidersStates ? statesOffered : [],
    [
      ...(Array.isArray(confirmed) ? confirmed : []),
      ...(Array.isArray(pending) ? pending : [])
    ]
  );

  const options = getCarrierStateOptions(carrierData, user, tempOptions);

  const statesAvailableButNoLicenses =
    tempOptions.length > 0 && options.length === 0;

  const showAppointmentOptionsAndActions =
    !hasOpenTransferRequests &&
    !areApptsDisabled &&
    !statesAvailableButNoLicenses &&
    !isSuncareAppointmentBlocked;

  // the shortcut is only worth showing while the agent still has something to submit below
  const hasActionableRequestSection =
    carrierConsidersStates &&
    !simplified &&
    !isAgencyDisabled &&
    !isSuncareAppointmentBlocked &&
    (showAppointmentOptionsAndActions || isFfmCertificationBlocked);

  const isSendRequestButtonInView = useIsElementInView(sendRequestButtonRef, [
    carrier,
    carrierConsidersStates,
    hasActionableRequestSection
  ]);

  const showScrollToRequestButton =
    hasActionableRequestSection && !isSendRequestButtonInView;

  const renderNotesAndComments = () => {
    return (
      <>
        {carrierData?.customNoteAddStates &&
          Array.isArray(confirmed) &&
          confirmed.length > 0 && (
            <div
              className={
                'mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'
              }
            >
              <p
                className={
                  'text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'
                }
              >
                Additional States Note
              </p>
              <Stack>
                <div
                  className={'text-left'}
                  dangerouslySetInnerHTML={sanitizeDirtyHTML(
                    carrierData.customNoteAddStates
                  )}
                />
              </Stack>
            </div>
          )}
      </>
    );
  };

  const showAppointedSidebarConfirmedDisclaimer = useMemo(() => {
    const disclaimer = carrierData.disclaimer;
    if (!disclaimer) return false;

    const placements = disclaimer.placements || new Set();

    const hasConfirmed = placements.has(
      DISCLAIMER_PLACEMENTS.APPOINTED_SIDEBAR_CONFIRMED
    );
    const hasOpen = placements.has(
      DISCLAIMER_PLACEMENTS.APPOINTED_SIDEBAR_OPEN
    );

    return (
      (hasConfirmed && hasOpen) ||
      (hasConfirmed && isAgentAppointed) ||
      (hasOpen && carrierReqAppts.length > 0)
    );
  }, [carrierData.disclaimer, carrier, user, carrierReqAppts.length]);

  return (
    <>
      <div className='w-full min-w-0'>
        {showAppointedSidebarConfirmedDisclaimer && (
          <div className='mb-2.5 w-full min-w-0'>
            <CarrierDisclaimer disclaimer={carrierData.disclaimer} />
          </div>
        )}
        {carrierConsidersStates ? (
          <>
            <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
              <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
                {company === 'oneill' ? 'Confirmed' : 'Active'}
              </p>
              {confirmed.length > 0 ? (
                <div
                  className={cn(
                    'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal [&>button]:cursor-pointer [&>button]:rounded-2xl [&>button]:border-none [&>button]:px-2 [&>button]:py-0.5 [&>button]:text-base [&>button]:font-normal [&>button:hover]:opacity-80 [&>button]:transition-opacity [&>button:hover]:duration-300',
                    '[&>span]:bg-secondary/20 [&>span]:text-secondary'
                  )}
                >
                  {confirmed.map((confirmedState, i) => (
                    <span key={`${confirmedState}${i}`}>{confirmedState}</span>
                  ))}
                </div>
              ) : (
                <div className='text-grey-light mt-2.5 block'>
                  <span>No confirmed appointments</span>
                </div>
              )}
            </div>

            <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
              <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
                <span>Pending</span>{' '}
                {isOneill && carrierReqAppts.length > 0 && (
                  <Tooltip
                    title='Click on any badge to view the request details and actions.'
                    placement='top'
                    arrow
                  >
                    <Info
                      size={18}
                      style={{
                        verticalAlign: 'middle',
                        color: '#d0d0d0'
                      }}
                    />
                  </Tooltip>
                )}
              </p>

              {isOneill && carrierReqAppts.length > 0 ? (
                <div
                  className={cn(
                    'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal [&>button]:cursor-pointer [&>button]:rounded-2xl [&>button]:border-none [&>button]:px-2 [&>button]:py-0.5 [&>button]:text-base [&>button]:font-normal [&>button:hover]:opacity-80 [&>button]:transition-opacity [&>button:hover]:duration-300',
                    'mt-2.5 [&>span]:!bg-[var(--color-semantic-warning)] [&>span]:!text-[var(--color-warning-contrast)] [&>button]:!bg-[var(--color-semantic-warning)] [&>button]:!text-[var(--color-warning-contrast)]'
                  )}
                >
                  {carrierReqAppts.map(appt => {
                    const Icon = isAppointmentActionable(appt)
                      ? TriangleAlert
                      : Info;

                    return (
                      <Tooltip
                        key={appt.id}
                        title={appt.substatus || ''}
                        placement='top'
                        arrow
                      >
                        <button onClick={() => setAppointmentDetails(appt)}>
                          {appt.substatus && (
                            <span
                              style={{
                                paddingRight: '8px'
                              }}
                            >
                              <Icon
                                size={18}
                                style={{
                                  verticalAlign: 'middle',
                                  color: isAppointmentActionable(appt)
                                    ? 'red'
                                    : undefined
                                }}
                              />
                            </span>
                          )}
                          <span
                            style={appt.substatus ? { paddingLeft: '0px' } : {}}
                          >
                            {getPendingApptBadgeLabel(appt)}
                          </span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              ) : pending.length > 0 ? (
                <div
                  className={cn(
                    'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal [&>button]:cursor-pointer [&>button]:rounded-2xl [&>button]:border-none [&>button]:px-2 [&>button]:py-0.5 [&>button]:text-base [&>button]:font-normal [&>button:hover]:opacity-80 [&>button]:transition-opacity [&>button:hover]:duration-300',
                    'mt-2.5 [&>span]:!bg-[var(--color-semantic-warning)] [&>span]:!text-[var(--color-warning-contrast)] [&>button]:!bg-[var(--color-semantic-warning)] [&>button]:!text-[var(--color-warning-contrast)]'
                  )}
                >
                  {pending.map((pendingState, i) => (
                    <span key={`${pendingState}${i}`}>{pendingState}</span>
                  ))}
                </div>
              ) : (
                carrierConsidersStates && (
                  <div className={'text-grey-light mt-2.5 block'}>
                    <span>No pending appointments</span>
                  </div>
                )
              )}
            </div>

            {showScrollToRequestButton && (
              <ScrollToRequestButton
                text='Request Appointments'
                onClick={scrollToRequestSection}
              />
            )}

            <CarrierFeatureNotes
              notes={carrierData.carrierFeatureDescriptionNotes}
            />

            {carrierReqAppts.length > 0 && renderNotesAndComments()}

            <div
              ref={requestSectionRef}
              className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'
            >
              {isSuncarePendingDirector && <SuncareInProgressNotice />}
              {isSuncareTrainingPending && (
                <SuncareTrainingNotice userEmail={user?.Email} />
              )}

              {!isSuncareAppointmentBlocked && (
                <>
                  <p className='text-black text-base font-medium tracking-wide mb-3 mt-3 text-left m-0'>
                    {isOneill
                      ? 'Request New Appointments'
                      : 'Request Appointments'}
                  </p>

                  {isAgencyDisabled && (
                    <p className='text-left leading-tight text-semantic-danger m-0 mb-3'>
                      {AGENCY_DISABLED_CARRIER_MESSAGE}
                    </p>
                  )}

                  {!isAgencyDisabled &&
                    showAppointmentOptionsAndActions &&
                    carrierData.allowTransferTypeSelection &&
                    isOneill && (
                      <p className='text-left leading-tight'>
                        Choose a transfer type only if you&apos;re moving an
                        existing appointment to {companyNames.short}, then click
                        Send Request
                      </p>
                    )}

                  {!isAgencyDisabled && showAppointmentOptionsAndActions && (
                    <StateSelector
                      formData={formData}
                      handleChangeAppointments={handleChangeAppointments}
                      options={options}
                    />
                  )}

                  {!isAgencyDisabled &&
                    showAppointmentOptionsAndActions &&
                    carrierData.allowTransferTypeSelection && (
                      <TransferTypeSelector
                        handleChangeAppointedType={handleChangeAppointedType}
                        formData={formData}
                        carrierName={carrier}
                      />
                    )}

                  {!isAgencyDisabled && (
                    <AppointmentMessages
                      isInitialOnboardingFormIncomplete={!obFormCompleted}
                      isFfmCertificationBlocked={isFfmCertificationBlocked}
                      statesAvailableButNoLicenses={
                        statesAvailableButNoLicenses
                      }
                      isBlockedByBcbsCrossRequest={isBlockedByBcbsCrossRequest}
                      bcbsCrossRequestBlockMessage={
                        bcbsCrossRequestBlockMessage
                      }
                      isCarrierDisabledDueToBlackout={
                        isCarrierDisabledDueToBlackout
                      }
                      carrierDisabledDueToBlackoutMessage={
                        carrierDisabledDueToBlackoutMessage
                      }
                    />
                  )}

                  {!simplified &&
                    !isAgencyDisabled &&
                    (showAppointmentOptionsAndActions ||
                      isFfmCertificationBlocked) && (
                      <SendRequestButton
                        ref={sendRequestButtonRef}
                        isDisabled={isDisabled}
                        isSendingRequestAppointments={
                          isSendingRequestAppointments
                        }
                        onSubmit={() => handleSubmit(formData.appointments)}
                      />
                    )}
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
              <h4 className='text-base font-medium text-black m-0 mb-2'>
                Status
              </h4>
              <p>
                {confirmed === true && !isSuncareTrainingPending ? (
                  <div
                    className={cn(
                      'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal [&>button]:cursor-pointer [&>button]:rounded-2xl [&>button]:border-none [&>button]:px-2 [&>button]:py-0.5 [&>button]:text-base [&>button]:font-normal [&>button:hover]:opacity-80 [&>button]:transition-opacity [&>button:hover]:duration-300',
                      '[&>span]:bg-secondary-light [&>span]:text-secondary-darkest'
                    )}
                  >
                    <span>{company === 'oneill' ? 'Confirmed' : 'Active'}</span>
                  </div>
                ) : isSuncarePendingDirector ? (
                  <div
                    className={cn(
                      'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal',
                      'mt-2.5 [&>span]:!bg-[var(--color-semantic-warning)] [&>span]:!text-[var(--color-warning-contrast)]'
                    )}
                  >
                    <span>
                      {getSuncareCardStatus(SUNCARE_PHASES.pendingDirector)}
                    </span>
                  </div>
                ) : isSuncareTrainingPending ? (
                  <div
                    className={cn(
                      'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal',
                      'mt-2.5',
                      getSuncareTrainingBadgeClassName()
                    )}
                  >
                    <span>
                      {getSuncareCardStatus(SUNCARE_PHASES.uploadCertificate)}
                    </span>
                  </div>
                ) : isOneill && carrierReqAppts.length > 0 ? (
                  <div
                    className={cn(
                      'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal [&>button]:cursor-pointer [&>button]:rounded-2xl [&>button]:border-none [&>button]:px-2 [&>button]:py-0.5 [&>button]:text-base [&>button]:font-normal [&>button:hover]:opacity-80 [&>button]:transition-opacity [&>button:hover]:duration-300',
                      'mt-2.5 [&>span]:!bg-[var(--color-semantic-warning)] [&>span]:!text-[var(--color-warning-contrast)] [&>button]:!bg-[var(--color-semantic-warning)] [&>button]:!text-[var(--color-warning-contrast)]'
                    )}
                  >
                    <Tooltip
                      title={noStatesActionableAppt.substatus || ''}
                      placement='top'
                      arrow
                    >
                      <button
                        onClick={() =>
                          setAppointmentDetails(noStatesActionableAppt)
                        }
                      >
                        {noStatesActionableAppt.substatus && (
                          <span
                            style={{
                              paddingRight: '8px'
                            }}
                          >
                            <TriangleAlert
                              size={18}
                              style={{
                                verticalAlign: 'middle',
                                color: isAppointmentActionable(
                                  noStatesActionableAppt
                                )
                                  ? 'red'
                                  : ''
                              }}
                            />
                          </span>
                        )}
                        <span
                          style={
                            noStatesActionableAppt.substatus
                              ? { paddingLeft: '0px' }
                              : {}
                          }
                        >
                          Pending
                        </span>
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  <div
                    className={cn(
                      'flex flex-wrap items-center gap-1.5 [&>span]:rounded-2xl [&>span]:border-none [&>span]:px-2 [&>span]:py-0.5 [&>span]:text-base [&>span]:font-normal [&>button]:cursor-pointer [&>button]:rounded-2xl [&>button]:border-none [&>button]:px-2 [&>button]:py-0.5 [&>button]:text-base [&>button]:font-normal [&>button:hover]:opacity-80 [&>button]:transition-opacity [&>button:hover]:duration-300',
                      'mt-2.5 [&>span]:!bg-[var(--color-semantic-warning)] [&>span]:!text-[var(--color-warning-contrast)] [&>button]:!bg-[var(--color-semantic-warning)] [&>button]:!text-[var(--color-warning-contrast)]'
                    )}
                  >
                    <span>Pending</span>
                  </div>
                )}
              </p>
              {isOneill && carrierReqAppts.length > 0 && (
                <Button
                  variant='primaryLight'
                  size='small'
                  text='See Request Details'
                  fill
                  onClick={() => setAppointmentDetails(noStatesActionableAppt)}
                />
              )}
            </div>
            <CarrierFeatureNotes
              notes={carrierData.carrierFeatureDescriptionNotes}
            />
            {isSuncarePendingDirector && (
              <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
                <SuncareInProgressNotice />
              </div>
            )}
            {isSuncareTrainingPending && (
              <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'>
                <SuncareTrainingNotice userEmail={user?.Email} />
              </div>
            )}
            {confirmed !== true && !isSuncareAppointmentBlocked && (
              <>
                {renderNotesAndComments()}
                <div className='mt-2.5 pt-2.5 border-t border-grey-lightest first:mt-0 first:border-t-0 first:pt-0'></div>
              </>
            )}
          </>
        )}

        {isOpenSBEModal && (
          <SBEModal
            requestedStates={formData.appointments}
            closeModal={() => setIsOpenSBEModal(false)}
            handleSubmit={handleSubmit}
            isLoading={isSendingRequestAppointments}
          />
        )}
      </div>

      {isOpenRequiredActionsModal && (
        <RequiredActionsModal
          handleClick={() => handleSendRequest(formData.appointments)}
          isLoading={isSendingRequestAppointments}
          isOpen={isOpenRequiredActionsModal}
          closeModal={() => setIsOpenRequiredActionsModal(false)}
          requiredActions={filteredActions || []}
          handleFilesChange={handleFilesChange}
          formData={formData}
          handleExpirationDatesChange={handleExpirationDatesChange}
          setAcknowledgement={handleAcknowledgement}
          userEmail={user?.Email}
          isSuncareContractStep={isSuncareContractStep}
          onSendContract={handleSendContract}
        />
      )}

      {appointmentDetails && (
        <AppointmentDetails
          appointmentDetails={appointmentDetails}
          setAppointmentDetails={setAppointmentDetails}
          carrierConsidersStates={carrierConsidersStates}
          newAppointmentsVideoURL={carrierData?.newAppointmentsVideoURL}
        />
      )}
    </>
  );
});

SideSection.propTypes = {
  carrier: propTypes.string,
  confirmed: propTypes.array,
  pending: propTypes.array,
  carrierData: propTypes.object,
  statesOffered: propTypes.array,
  carrierConsidersStates: propTypes.bool,
  createAppointmentRequest: propTypes.func,
  obFormCompleted: propTypes.bool,
  simplified: propTypes.bool,
  setApptRequestStatus: propTypes.func,
  carrierInProgress: propTypes.func,
  hasOpenTransferRequests: propTypes.bool,
  areApptsDisabled: propTypes.bool,
  isAgencyDisabled: propTypes.bool
};

export default SideSection;
